import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchBlogsAPI,
  fetchBlogByIdAPI,
  deleteBlogAPI,
  updateBlogAPI,
  createBlogAPI,
  publishBlogAPI,
  unpublishBlogAPI,
  archiveBlogAPI,
  unarchiveBlogAPI,
} from "../../services/blogsCommunityAPI";

// ✅ Fetch all blogs with pagination
export const fetchBlogs = createAsyncThunk(
  "blogs/fetchBlogs",
  async (page = 1) => {
    const response = await fetchBlogsAPI(page);
    return { data: response.data.data, pagination: response.data.pagination };
  }
);

// ✅ Fetch single blog by ID
export const fetchBlogById = createAsyncThunk(
  "blogs/fetchBlogById",
  async (id) => {
    const response = await fetchBlogByIdAPI(id);
    return response.data;
  }
);

// ✅ Delete blog
export const deleteBlog = createAsyncThunk(
  "blogs/deleteBlog",
  async (id) => {
    await deleteBlogAPI(id);
    return id;
  }
);

// ✅ Update blog
export const updateBlog = createAsyncThunk(
  "blogs/updateBlog",
  async ({ id, data }) => {
    const response = await updateBlogAPI(id, data);
    return response.data;
  }
);

// ✅ Create blog
export const createBlog = createAsyncThunk(
  "blogs/createBlog",
  async (data) => {
    const response = await createBlogAPI(data);
    return response.data;
  }
);

// ✅ Publish blog
export const publishBlog = createAsyncThunk(
  "blogsCommunity/publishBlog",
  async (id) => {
    await publishBlogAPI(id);
    return id;
  }
);

// ✅ Unpublish blog
export const unpublishBlog = createAsyncThunk(
  "blogsCommunity/unpublishBlog",
  async (id) => {
    await unpublishBlogAPI(id);
    return id;
  }
);

// ✅ Archive blog
export const archiveBlog = createAsyncThunk(
  "blogsCommunity/archiveBlog",
  async (id) => {
    await archiveBlogAPI(id);
    return id;
  }
);

// ✅ Unarchive blog
export const unarchiveBlog = createAsyncThunk(
  "blogsCommunity/unarchiveBlog",
  async (id) => {
    await unarchiveBlogAPI(id);
    return id;
  }
);

const initialState = {
  items: [],
  detail: null,
  status: "idle",        // for list (fetchBlogs)
  detailStatus: "idle",  // for fetchBlogById
  error: null,
  // Pagination state
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  }
};

const blogsCommunitySlice = createSlice({
  name: "blogs",
  initialState,
  reducers: {
    clearDetail: (state) => {
      state.detail = null;
      state.detailStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // ✅ Fetch all
      .addCase(fetchBlogs.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchBlogs.fulfilled, (state, action) => {
        state.status = "succeeded";
        const { data, pagination } = action.payload;
        const blogs = data.blogs;
        state.items = blogs.map(b => ({
          uuid: b.uuid,
          title: b.title,
          author: b.author?.name || 'NA',
          category: b.category,
          submitted: b.published_at ? new Date(b.published_at).toISOString().slice(0, 10) : (
            b.status === 'draft' ? 'Draft' :
            b.status === 'archived' ? 'Archived' :
            'Published'
          ),
          status: b.status,
          content: b.content,
          published_at: b.published_at,
          author_details: b.author, // full author object
        }));
        // Update pagination
        if (pagination) {
          state.pagination = {
            currentPage: pagination.current_page || 1,
            totalPages: pagination.total_pages || 1,
            totalItems: pagination.total_count || 0,
            itemsPerPage: pagination.per_page || 10
          };
        }
      })
      .addCase(fetchBlogs.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })

      // ✅ Fetch single
      .addCase(fetchBlogById.pending, (state) => {
        state.detailStatus = "loading";
        state.detail = null;
      })
      .addCase(fetchBlogById.fulfilled, (state, action) => {
        state.detailStatus = "succeeded";
        state.detail = action.payload.data;
      })
      .addCase(fetchBlogById.rejected, (state, action) => {
        state.detailStatus = "failed";
        state.error = action.error.message;
      })

      // ✅ Delete
      .addCase(deleteBlog.fulfilled, (state, action) => {
        const id = action.payload;
        state.items = state.items.filter((b) => b.id !== id);
        if (state.detail?.id === id) {
          state.detail = null;
          state.detailStatus = "idle";
        }
      })

      // ✅ Update
      .addCase(updateBlog.fulfilled, (state, action) => {
        state.detail = action.payload; // refresh detail
        state.items = state.items.map((b) =>
          b.id === action.payload.id ? action.payload : b
        );
      })

      // ✅ Create
      .addCase(createBlog.fulfilled, (state, action) => {
        const newBlog = action.payload;
        // Transform the status and submitted field for display
        const transformedBlog = {
          ...newBlog,
          status: newBlog.status,
          submitted: newBlog.published_at ? new Date(newBlog.published_at).toISOString().slice(0, 10) : (
            newBlog.status === 'draft' ? 'Draft' :
            newBlog.status === 'archived' ? 'Archived' :
            'Published'
          )
        };
        state.items.unshift(transformedBlog); // Add to beginning of list
      })

      // ✅ Publish blog
      .addCase(publishBlog.fulfilled, (state, action) => {
        const { id, status } = action.payload;
        // Update in items list
        state.items = state.items.map((b) =>
          b.uuid === id ? { ...b, status } : b
        );
        // Update detail if it's the current blog
        if (state.detail?.uuid === id) {
          state.detail.status = status;
        }
      })

      // ✅ Unpublish blog
      .addCase(unpublishBlog.fulfilled, (state, action) => {
        const { id, status } = action.payload;
        state.items = state.items.map((b) =>
          b.uuid === id ? { ...b, status } : b
        );
        if (state.detail?.uuid === id) {
          state.detail.status = status;
        }
      })

      // ✅ Archive blog
      .addCase(archiveBlog.fulfilled, (state, action) => {
        const { id, status } = action.payload;
        state.items = state.items.map((b) =>
          b.uuid === id ? { ...b, status } : b
        );
        if (state.detail?.uuid === id) {
          state.detail.status = status;
        }
      })

      // ✅ Unarchive blog
      .addCase(unarchiveBlog.fulfilled, (state, action) => {
        const { id, status } = action.payload;
        state.items = state.items.map((b) =>
          b.uuid === id ? { ...b, status } : b
        );
        if (state.detail?.uuid === id) {
          state.detail.status = status;
        }
      });
  },
});

export const { clearDetail } = blogsCommunitySlice.actions;
export default blogsCommunitySlice.reducer;
